import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { projectApiUrl } from "../../constants";
import { AuthContext } from "../../contexts/AuthContext";
import type { LayoutOutletContext } from "../../components/Layout/Layout";
import { formatUserDateTime } from "./dateTime.utils";
import { useButtonTitles } from "./useButtonTitles";
import "./PersonalData.css";

const ITEMS_PER_PAGE = 20;

const getActivityTitle = (log: Record<string, any>, t: ReturnType<typeof useTranslation>["t"]) => {
  const activityType = log.activityType ? String(log.activityType) : "Activity";
  if (log.description && !log.activityType) return String(log.description);
  return t(`personalData.activityLogs.activityTypes.${activityType}`, { defaultValue: activityType });
};

const getSourceTitle = (source: unknown, t: ReturnType<typeof useTranslation>["t"]) => {
  const value = String(source || "");
  if (value === "id_express") return t("personalData.activityLogs.sources.id_express", { defaultValue: "ID.Express" });
  if (value === "personal_data") return t("personalData.activityLogs.sources.personal_data", { defaultValue: "Personal data" });
  return formatField(value);
};

const formatField = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const ActivityLogs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { AuthContextState } = useContext(AuthContext);
  const { theme } = useOutletContext<LayoutOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const backToSecurityLabel = t("personalData.activityLogs.backToSecurity", { defaultValue: "Back to security" });
  const buttonTitlesRef = useButtonTitles(backToSecurityLabel);

  const currentPage = useMemo(() => {
    const page = Number(searchParams.get("current_page") || searchParams.get("page") || "1");
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }, [searchParams]);

  const authHeaders = useMemo<Record<string, string>>(() => {
    const headers: Record<string, string> = {};
    if (AuthContextState.accessToken) {
      headers.Authorization = `Bearer ${AuthContextState.accessToken}`;
    }
    return headers;
  }, [AuthContextState.accessToken]);

  const fetchLogs = async (page: number) => {
    const activityLogsBaseUrl = projectApiUrl;

    if (!activityLogsBaseUrl) {
      setError(t("personalData.activityLogs.error.noApiUrl", { defaultValue: "API URL is not configured." }));
      setLoading(false);
      return;
    }

    const activityLogsUrl = `${activityLogsBaseUrl.replace(/\/$/, "")}/personal_data/activity_logs`;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(activityLogsUrl, {
        headers: authHeaders,
        params: {
          current_page: page,
          items_per_page: ITEMS_PER_PAGE,
          service: "all",
        },
        withCredentials: true,
      });

      const payload = response.data || {};
      setLogs(Array.isArray(payload.data) ? payload.data : []);
      setCount(typeof payload.count === "number" ? payload.count : 0);
      setTotalPages(typeof payload.total_pages === "number" ? payload.total_pages : 0);
    } catch (fetchError) {
      const message = axios.isAxiosError(fetchError)
        ? fetchError.response?.data?.message || fetchError.message
        : String(fetchError);
      setError(String(message || "Failed to load activity logs."));
      setLogs([]);
      setCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(currentPage);
  }, [currentPage, authHeaders]);

  const handlePageChange = (page: number) => {
    setSearchParams({ current_page: String(page) });
  };

  return (
    <div ref={buttonTitlesRef} className={`pd-shell ${theme === "dark" ? "pd-theme-dark" : "pd-theme-light"}`} data-theme={theme}>
      <div className="pd-shell__noise" />
      <div className="pd-shell__ambient pd-shell__ambient--one" />
      <div className="pd-shell__ambient pd-shell__ambient--two" />

      <div className="pd-dashboard">
        <div className="pd-notice-banner pd-notice-banner--info" style={{ marginBottom: "20px" }}>
          {t("personalData.security.activityLogDescription", { defaultValue: "Check recent account security events and authorization actions." })}
        </div>

        <section className="pd-form-shell pd-glass pd-activity-logs-panel">
          <div className="pd-form-shell__header">
            <div>
              <h2>{t("personalData.activityLogs.title", { defaultValue: "Activity log" })}</h2>
              <p>
                {t("personalData.activityLogs.description", {
                  defaultValue: "Review your current and linked account activity from both personal data and authentication services.",
                })}
              </p>
            </div>
            <div className="pd-topbar__actions">
              <button type="button" className="pd-secondary-button" title={backToSecurityLabel} onClick={() => navigate(`/${i18n.language}/personal_data?section=security`)}>
                {backToSecurityLabel}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="pd-shimmer-stack" aria-hidden="true">
              <div className="pd-shimmer pd-shimmer--hero" />
              <div className="pd-shimmer pd-shimmer--line" />
              <div className="pd-shimmer pd-shimmer--line" />
              <div className="pd-shimmer pd-shimmer--line short" />
            </div>
          ) : error ? (
            <div className="pd-notice-banner pd-notice-banner--error">{error}</div>
          ) : (
            <>
              <div className="pd-activity-logs-summary">
                <span>{t("personalData.activityLogs.summary", { defaultValue: "Showing recent activity" })}</span>
                <span>{`${count} ${t("personalData.activityLogs.entries", { defaultValue: "entries" })}`}</span>
              </div>

              {logs.length === 0 ? (
                <div className="pd-empty-state">
                  <p>{t("personalData.activityLogs.empty", { defaultValue: "No activity log entries found." })}</p>
                </div>
              ) : (
                <div className="pd-activity-log-table-wrapper">
                  <table className="pd-activity-log-table">
                    <thead>
                      <tr>
                        <th>{t("personalData.activityLogs.table.timestamp", { defaultValue: "Timestamp" })}</th>
                        <th>{t("personalData.activityLogs.table.activity", { defaultValue: "Activity" })}</th>
                        <th>{t("personalData.activityLogs.table.source", { defaultValue: "Source" })}</th>
                        <th>{t("personalData.activityLogs.table.performedBy", { defaultValue: "Performed by" })}</th>
                        <th>{t("personalData.activityLogs.table.details", { defaultValue: "Details" })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, index) => (
                        <tr key={`${log._id || index}-${log.timestamp || index}`}>
                          <td>{formatUserDateTime(log.timestamp, i18n.language)}</td>
                          <td>{getActivityTitle(log, t)}</td>
                          <td>{getSourceTitle(log.source, t)}</td>
                          <td>{formatField(log.performedBy)}</td>
                          <td>
                            <div className="pd-activity-log-details">
                              <p>{t("personalData.activityLogs.details.ipAddress", { defaultValue: "IP" })}: {formatField(log.ipAddress)}</p>
                              <p>{t("personalData.activityLogs.details.location", { defaultValue: "Location" })}: {formatField(log.location)}</p>
                              <p>{t("personalData.activityLogs.details.device", { defaultValue: "Device" })}: {formatField(log.deviceDetails || log.deviceType)}</p>
                              <p>{t("personalData.activityLogs.details.browser", { defaultValue: "Browser" })}: {formatField(log.browser)}</p>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div className="pd-pagination">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`pd-secondary-button ${page === currentPage ? "is-active" : ""}`}
                      onClick={() => handlePageChange(page)}
                      disabled={page === currentPage}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ActivityLogs;
